/* SiteMultiEditViewRenderer.js */

"use strict";
const _ = require("lodash");
const ButtonControl = require("@gluenetworks/steve_ui_core").Control.Action.Button;
const Grid = require("@gluenetworks/steve_ui_widgets").GridGl.Grid;
const FormsComponent = require("@gluenetworks/forms").ui.multiEditComponent;
const moment = require("moment");
require("moment-duration-format");

const readOnlyFieldIds = [];
class SiteMultiEditViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("site-multiedit-view-gl");
        this._rootElem.component = this._model;

        this._detailSection = document.createElement("div");
        this._detailSection.classList.add("form-section");

        this._selectedSitesSection = document.createElement("div");
        this._selectedSitesSection.classList.add("selected-site-view-gl", "hidden");

        this._logger = this._model.logger;
        this._siteCfUtility = this._model._siteCfUtility;

        this._formsComponent = new FormsComponent();
        this._viewButtonContainer = document.createElement("view-button-container");
        this._viewAllButton = new ButtonControl({
            label: "View All",
            darkTheme: false,
        });
        this._viewButtonContainer.appendChild(this._viewAllButton.renderer.rootElement);
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Save",
            name: "SaveSiteDetails",
            handler: (this._onSiteDetailsSave = this._onSiteDetailsSave.bind(this)),
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelSiteDetails",
            handler: (this._onSiteDetailsCancel = this._onSiteDetailsCancel.bind(this)),
        });

        let columnConfig = {
            name: {
                label: "Selected Sites",
                width: 0.2,
            },
            description: {
                label: "Description",
                width: 0.8,
            },
        };

        this._selectListGrid = new Grid({
            columns: columnConfig,
            showHeader: true,
            columnOrder: ["name", "description"],
        });
        this._gridRows = {};

        this._obsConfig = {attributes: true, childList: false, subtree: true};

        this._ObsCallback = (mutationList) => {
            for (const mutation of mutationList) {
                if (mutation.target.tagName === "INPUT-POWERBUTTON-CONTROL") {
                    this._onMultiEditActive();
                }
            }
        };

        this._muObserver = new MutationObserver(this._ObsCallback);

        this._renderingForm = false;
        this._toggleActions();
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on(
            "EditorModeChanged",
            (this._onEditorModeChanged = this._onEditorModeChanged.bind(this))
        );
        this._model.on("Dirty", (this._onDirty = this._onDirty.bind(this)));

        this._viewAllButton.on(
            "UserClick",
            (this._onViewButtonClick = this._onViewButtonClick.bind(this))
        );

        this._model.on(
            "SitePropertiesChanged",
            (this._onSitePropertiesChanged = this._onSitePropertiesChanged.bind(this))
        );
        this._registerFormsListener();
    }

    _unregisterListeners() {
        this._model.removeListener("EditorModeChanged", this._onEditorModeChanged);
        this._model.removeListener("Dirty", this._onDirty);
        this._model.removeListener("SitePropertiesChanged", this._onSitePropertiesChanged);
        this._viewAllButton.removeListener("UserClick", this._onViewButtonClick);
        this._unregisterFormsListener();
    }

    _registerFormsListener() {
        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = _.debounce(
                this._onFormsPendingPatchesChanged.bind(this),
                500,
                false
            ))
        );
    }

    _registerFormsApiListener() {
        this._formsComponent.renderer._formRendererApi.on(
            "MultiEditActive",
            (this._onMultiEditActive = this._onMultiEditActive.bind(this))
        );
    }

    _unregisterFormsApiListener() {
        this._formsComponent.renderer._formRendererApi.removeListener(
            "MultiEditActive",
            this._onMultiEditActive
        );
    }

    _unregisterFormsListener() {
        this._formsComponent.removeListener(
            "PendingPatchesChanged",
            this._onFormsPendingPatchesChanged
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();
        this._muObserver.disconnect();
        // this._unregisterFormsApiListener();
        this._onFormsPendingPatchesChanged.flush();
        let promArr = [this._formsComponent.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._viewAllButton = undefined;
                this._viewButtonContainer.remove();
                this._viewButtonContainer = undefined;
                this._detailSection.remove();
                this._detailSection = undefined;
                this._selectedSitesSection.remove();
                this._selectedSitesSection = undefined;
                this._selectListGrid.shutdown().then(() => {
                    this._selectListGrid = undefined;
                });
            },
            (err) => {
                this._logger.warn({error: err}, "SiteDetailsViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        return Promise.resolve()
            .then(() => this._showLoader())
            .then(() => this._model.whenModelIsReady())
            .then(() => {
                this._renderSiteDetails();
                this._renderGridRows();
                if (!this._rootElem.hasChildNodes()) {
                    this._detailSection.appendChild(this._viewButtonContainer);
                    this._rootElem.appendChild(this._detailSection);
                    this._rootElem.appendChild(this._selectedSitesSection);
                }
            })
            .then(() => this._hideModal())
            .catch((err) => {
                if (err === "Sites no longer exist") {
                    return this.model.gameEditor.closeEditor();
                } else {
                    return this._showError(err);
                }
            })
            .then(() => this._toggleActions());
    }

    _onEditorModeChanged() {
        return Promise.resolve().then(() => {
            if (this._model.editorMode === "viewall") {
                this._renderGridRows();
                this._detailSection.classList.add("hidden");
                this._selectedSitesSection.classList.remove("hidden");
                this._renderBackButton();
            } else {
                this._detailSection.classList.remove("hidden");
                this._selectedSitesSection.classList.add("hidden");
            }
        });
    }

    _onMultiEditActive() {
        const iconEls = this._rootElem.querySelectorAll(".cf-hierarchy base-icon-gl");
        this._siteCfUtility.removeInheritIconListenersME(iconEls);
        this._discoveryLevelInheritanceUtility.removeEventListenerMe();
        iconEls.forEach((el) => {
            el.classList.remove("enabled");
        });
        const formValues = this._formsComponent.renderer._formRendererApi.getValue();
        if (formValues && formValues.customFieldValues) {
            Object.keys(formValues.customFieldValues).forEach((key) => {
                if (this._model._inheritableCfs.indexOf(key) >= 0) {
                    const cfEl = this._rootElem.querySelector(
                        "." + CSS.escape(key.replaceAll("-", "_"))
                    );
                    if (!cfEl) {
                        return;
                    }
                    const iconEl = cfEl.querySelector(".cf-hierarchy base-icon-gl");
                    iconEl.classList.add("enabled");
                    this._siteCfUtility.addInheritIconListenerME(iconEl);
                }
            });
        }

        if (formValues && formValues.discoveryLevel) {
            this._discoveryLevelInheritanceUtility.addEventListenerMe();
        }
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    _showViewActions() {
        this._model.gameEditor.showViewAction("SaveSiteDetails");
        this._model.gameEditor.showViewAction("CancelSiteDetails");
    }

    _hideViewActions() {
        this._model.gameEditor.hideViewAction("SaveSiteDetails");
        this._model.gameEditor.hideViewAction("CancelSiteDetails");
    }

    _disableViewActions() {
        this._model.gameEditor.disableViewAction("SaveSiteDetails");
        this._model.gameEditor.disableViewAction("CancelSiteDetails");
    }

    _enableViewActions() {
        this._model.gameEditor.enableViewAction("SaveSiteDetails");
        this._model.gameEditor.enableViewAction("CancelSiteDetails");
    }

    _toggleActions() {
        if (this._model.isDirty()) {
            this._showViewActions();
        } else {
            this._hideViewActions();
        }
    }
    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderSiteDetails() {
        this._model.setTabDirty(false);
        this._model._tabComponent.setTitle("Multi-Edit (" + this._model._sites.length + ")");

        return Promise.resolve()
            .then(
                () => {
                    let jtype = this._getJtype();
                    let pristineValues = this._getPristineValues();
                    this._formsComponent.setTypeInformation(jtype, true);
                    this._formsComponent.setPristineValue(pristineValues, true);
                    this._renderingForm = true;
                    return this._formsComponent.renderer.render().then(() => {
                        if (!this._detailSection.querySelector("forms-gl")) {
                            this._detailSection.appendChild(
                                this._formsComponent.renderer.rootElement
                            );
                            this._muObserver.disconnect();
                            this._muObserver.observe(
                                this._formsComponent.renderer.rootElement,
                                this._obsConfig
                            );
                        }
                        this._renderingForm = false;
                        // this._registerFormsApiListener();
                        this._siteCfIconsList = this._siteCfUtility.generateInheritanceIconsME(
                            this._rootElem,
                            this._model._inheritableCfs
                        );
                        this._discoveryLevelInheritanceUtility =
                            new this._model._InheritedFieldUtility(
                                "discoveryLevel",
                                "discovery_level",
                                undefined,
                                undefined,
                                this._formsComponent,
                                undefined,
                                undefined,
                                undefined
                            );
                        this._discoveryLevelInheritanceUtility.generateIconMe();
                    });
                },
                (err) => {
                    this._logger.warn(
                        {error: err},
                        "SiteDetailsViewRenderer._renderSiteDetails() error."
                    );
                }
            )
            .then(() => {
                return this._enableViewActions();
            });
    }
    _renderGridRows() {
        this._gridRows = {};
        let data = this._model._sites;
        data.forEach((obj) => {
            this._gridRows[obj.id] = {
                name: obj.name,
                description: obj.description,
            };
        });
        this._selectListGrid.setRows(this._gridRows);
        this._selectedSitesSection.appendChild(this._selectListGrid.renderer.rootElement);
    }
    _showLoader(options) {
        return this._model._modalUserFeedback.displayLoading(options);
    }

    _showError(error) {
        return this._model._modalUserFeedback.displayError(error);
    }

    _hideModal() {
        return this._model._modalUserFeedback.hideDisplayed();
    }

    _onViewButtonClick() {
        const selectedSite = this._model._sites;
        if (selectedSite.length > 0) {
            this._model.setEditorMode("viewall");
        } else {
            this._model._logger.warn(
                {},
                "User clicked on viewAll in the multiEditView does not have a Selected Site."
            );
        }
    }
    _renderBackButton() {
        const model = this._model;
        const bcs = [
            {
                content: backButton,
                left: {
                    arrow: {
                        direction: "left",
                    },
                },
            },
        ];
        this._model.gameEditor.setBreadCrumbs(bcs);

        return Promise.resolve();

        function backButton() {
            const el = document.createElement("span");
            el.innerHTML = "Multi-Edit";
            el.addEventListener("click", () => {
                // clear breadcrumbs link
                model.gameEditor.setBreadCrumbs([]);
                // change tab back to general
                model.setEditorMode("general");
            });
            // prettier-ignore
            this.renderer = { //jshint ignore:line
                rootElement: el
            };
        }
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onFormsPendingPatchesChanged() {
        if (!this._renderingForm) {
            //const form = this._formsComponent.getValue(),
            const isDirty = !_.isEmpty(this._getFormValue());
            this._toggleActions();
            this._model.setTabDirty(isDirty);
        }
    }

    _onSitePropertiesChanged(pristineProperties, before) {
        if (!this._renderingForm) {
            let formVal = this._formsComponent.getValue(); // Data from Jtype form
            let sitePropsVal = this._getPristineValue(); // Data from _pristineProperties i.e. sync'd with BE

            let updateReadOnlyFields = () => {
                _.keys(sitePropsVal).map((id) => {
                    if (readOnlyFieldIds.indexOf(id) === -1) {
                        sitePropsVal[id] = formVal[id];
                    }
                });
                this._formsComponent.setPristineValue(sitePropsVal);
                return Promise.resolve();
            };

            if (this._model.isDirty()) {
                let isReadOnlyUpdate = true;
                _.keys(pristineProperties).map((id) => {
                    if (
                        readOnlyFieldIds.indexOf(id) === -1 &&
                        !_.isEqual(
                            _.isEmpty(pristineProperties[id]) ? "" : pristineProperties[id],
                            _.isEmpty(before[id]) ? "" : before[id]
                        )
                    ) {
                        isReadOnlyUpdate = false;
                    }
                });
                if (!isReadOnlyUpdate) {
                    this._model.gameEditor.runQuickConfirmationActionWorkflow(
                        {
                            message:
                                "This site has been updated by another user. Do you want to refresh its details? All changes will be lost.",
                            title: "Update Confirmation Check",
                            confirmDetails: {
                                text: "Yes",
                                type: "text",
                            },
                        },
                        (this._onSiteDetailsCancelAction =
                            this._onSiteDetailsCancelAction.bind(this)),
                        updateReadOnlyFields
                    );
                } else {
                    updateReadOnlyFields();
                }
            } else {
                this._formsComponent.setPristineValue(sitePropsVal);
            }
        }
    }

    _onDirty() {
        this._toggleActions();
    }

    _onSiteDetailsSave() {
        this._disableViewActions();
        return Promise.resolve()
            .then(() => this._showLoader())
            .then(() => this._model.saveSite(this._getFormValue()))

            .catch((err) => this._showError(err))
            .catch((err = {}) => (err.userRejected ? Promise.resolve() : Promise.reject(err)))
            .then(() => {
                this._unregisterFormsListener();
                this._unregisterFormsApiListener();
                return this._formsComponent.shutdown();
            })
            .then(() => {
                this._formsComponent = new FormsComponent();
                this._registerFormsListener();
                return this._renderSiteDetails();
            })
            .then(() => this._hideModal())
            .then(() => _.isEmpty(this._model._sites) && this._model.closeTab())

            .catch((error) =>
                this._logger.warn({error}, "SiteDetailsViewRender._onSiteDetailsSave() error.")
            );
    }

    _onSiteDetailsCancel() {
        this._model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text",
                },
            },
            (this._onSiteDetailsCancelAction = this._onSiteDetailsCancelAction.bind(this))
        );
    }

    _onSiteDetailsCancelAction() {
        this._siteCfUtility.clearInheritedCfs();
        return Promise.resolve()
            .then(() => {
                this._unregisterFormsListener();
                this._unregisterFormsApiListener();
                return this._formsComponent.shutdown();
            })
            .then(() => {
                this._formsComponent = new FormsComponent();
                this._registerFormsListener();
                return this._renderSiteDetails();
            })
            .then(() => this._hideModal());
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _toLocaleString(val) {
        let d = new Date(val);
        if (Object.prototype.toString.call(d) === "[object Date]" && !isNaN(d.getTime())) {
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } else {
            return "";
        }
    }

    _getPristineValues() {
        return this._model._sites.map(this._getPristineValue.bind(this));
    }

    _getPristineValue(site) {
        if (!this._model) {
            return;
        }

        let pristineProperties = site,
            pristineValue = {};

        if (this._model._sites) {
            pristineValue.description = pristineProperties.description;
            pristineValue.adminContact = pristineProperties.adminContact || [];
            pristineValue.technicalContact = pristineProperties.technicalContact || [];
            pristineValue.location = pristineProperties.location;
            pristineValue.locationType = pristineProperties.locationType;
            pristineValue.address = pristineProperties.address;
            pristineValue.country = pristineProperties.country;
            pristineValue.latitude = pristineProperties.latitude;
            pristineValue.longitude = pristineProperties.longitude;
            pristineValue.timezone = pristineProperties.timezone;
            if (pristineProperties.customFieldValues) {
                pristineValue.customFieldValues = pristineProperties.customFieldValues;
            }
            pristineValue.discoveryLevel = pristineProperties.discoveryLevel;
            pristineValue.credsName = pristineProperties.credsName
                ? pristineProperties.credsName.toLowerCase().search("_site") >= 0
                    ? "_Site"
                    : pristineProperties.credsName
                : "";
        }
        return pristineValue;
    }

    _getJtype() {
        // Location type obj, update to get from entity
        const locTypes = this._model._siteEntityDef.getLocationTypeLabels();
        let customFields = this._model._customFieldColumnDef;

        let jtype = {};

        jtype.description = "";
        jtype["description@Display.label"] = "Description";

        jtype.adminContact = [];
        jtype["adminContact@Display.label"] = "Admin Contact";
        jtype["adminContact@Display.multiline"] = true;
        jtype["adminContact@Display.text"] = {minLineCount: 5};
        jtype["adminContact@Display.placeholder"] = "";

        jtype.technicalContact = [];
        jtype["technicalContact@Display.label"] = "Technical Contact";
        jtype["technicalContact@Display.multiline"] = true;
        jtype["technicalContact@Display.text"] = {minLineCount: 5};
        jtype["technicalContact@Display.placeholder"] = "";

        jtype.location = "";
        jtype["location@Display.label"] = "Location";

        jtype.locationType = "SITE";
        jtype["locationType@Display.label"] = "Type";

        const sortedLocTypes = Object.entries(locTypes).sort(([, a], [, b]) => a.localeCompare(b));
        jtype["locationType@Enum"] = sortedLocTypes.map(([key]) => key);
        jtype["locationType@Display.enum"] = sortedLocTypes.map(([, label]) => label);
        jtype["locationType@Required"] = true;

        jtype.address = "";
        jtype["address@Display.label"] = "Address";

        jtype.country = "";
        jtype["country@Display.label"] = "Country/Region";

        jtype.latitude = "";
        jtype["latitude@Display.label"] = "Latitude";

        jtype.longitude = "";
        jtype["longitude@Display.label"] = "Longitude";

        const timeZones = moment.tz.names();
        let offsetTmzLabels = [];
        let offsetTmzValues = [];
        for (let i in timeZones) {
            offsetTmzLabels.push(
                `(GMT${moment.tz(timeZones[i]).format("Z")}) ${timeZones[i].replaceAll("_", " ")}`
            );
            offsetTmzValues.push(`${timeZones[i]}`);
        }
        jtype["timezone@Enum"] = offsetTmzValues;
        jtype["timezone@Display.enum"] = offsetTmzLabels;
        jtype["timezone@Display.label"] = "Timezone";

        jtype.credsName = "";
        jtype["credsName@Enum"] = Object.keys(this._model.credOptions);
        jtype["credsName@Display.enum"] = Object.keys(this._model.credOptions);
        jtype["credsName@Display.label"] = "Credential";

        if (customFields) {
            //const customFieldsJtypeSection = this.model.customFieldsJtypes;
            //Object.assign(jtype, ...customFieldsJtypeSection);

            jtype.customFieldValues = {};
            jtype["customFieldValues@Display.label"] = "Custom Fields";
            jtype["customFieldValues@Display.readOnly"] = true;
            const customFieldValues = this.model.customFieldsJtypes;
            Object.assign(jtype.customFieldValues, ...customFieldValues);
        }
        jtype["discoveryLevel@Enum"] = Object.values(
            this._model._siteEntityDef.getDiscoveryLevelValues()
        ).map(Number); //must convert to number, Object.keys won't auto convert either

        jtype["discoveryLevel@Display.enum"] = Object.values(
            this._model._siteEntityDef.getDiscoveryLevelLabels()
        );
        jtype.discoveryLevel = "";
        jtype["discoveryLevel@Display.label"] = "Discovery Level";
        jtype["discoveryLevel@Required"] = true;
        //append readOnly props
        readOnlyFieldIds.forEach((readOnlyField) => {
            if (_.has(jtype, readOnlyField)) {
                jtype[`${readOnlyField}@Display.readOnly`] = true;
            }
        });

        return jtype;
    }

    _getFormValue() {
        return this._formsComponent.getFormValue();
    }
    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = SiteMultiEditViewRenderer;
